import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Observable, Subject, retry } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Color, ColorVariable, RGB } from '../models/color';
import {
  Flow,
  QuestionIncrementalRequest,
  QuestionRequest,
} from '../models/flow';
import { Mall, UserDocumentType } from '../models/mall';

export interface DocumentTypeOption {
  label: string;
  value: string;
}

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  mallLoaded: Subject<Mall> = new Subject<Mall>();
  loadingMall: boolean = true;
  mall?: Mall;
  document_type!: string;
  document_number!: string;

  private siteKey = '';

  private readonly defaultUserDocumentTypes: DocumentTypeOption[] = [
    {
      label: 'Cédula de Ciudadanía',
      value: 'CC',
    },
    {
      label: 'Cédula de extranjería',
      value: 'CE',
    },
    {
      label: 'Pasaporte',
      value: 'PA',
    },
    {
      label: 'NIT',
      value: 'NIT',
    },
    {
      label: 'Permiso especial',
      value: 'PEP',
    },
    {
      label: 'Permiso de protección temporal',
      value: 'PPT',
    },
  ];

  //   private response = [
  //     // {
  //     //     "name": "phone",
  //     //     "type": "number",
  //     //     "required": true,
  //     //     "label": "Celular"
  //     // },
  //     // {
  //     //     "name": "email",
  //     //     "type": "text",
  //     //     "required": false,
  //     //     "label": "Correo electrónico"
  //     // },
  //     // {
  //     //     "name": "first_name",
  //     //     "type": "text",
  //     //     "required": true,
  //     //     "label": "Nombre"
  //     // },
  //     // {
  //     //     "name": "last_name",
  //     //     "type": "text",
  //     //     "required": true,
  //     //     "label": "Apellidos"
  //     // },
  //     // {
  //     //     "name": "born_date",
  //     //     "type": "date",
  //     //     "required": false,
  //     //     "label": "Fecha de nacimiento"
  //     // },
  //     // {
  //     //     "name": "allow_promotional_emails",
  //     //     "type": "check",
  //     //     "required": false,
  //     //     "label": "Acepto recibir notificaciones por correo electrónico"
  //     // },
  //     // {
  //     //     "name": "allow_promotional_sms",
  //     //     "type": "check",
  //     //     "required": false,
  //     //     "label": "Acepto recibir notificaciones por SMS"
  //     // }
  // ]

  constructor(
    @Inject(DOCUMENT) private document: Document,
    public httpClient: HttpClient,
    private titleService: Title
  ) {}

  /**
   * Returns the mall object if it is already loaded, otherwise loads it from the server
   * @returns Promise<Mall>
   */
  loadMall(): Promise<Mall> {
    if (this.mall) return Promise.resolve(this.mall);
    return new Promise((resolve, reject) => {
      this.getMall().subscribe({
        next: (data) => {
          this.loadingMall = false;
          this.mall = data;
          this.setPageTitle(this.mall.name + ' | Sube tus facturas ');
          this.loadMallColors();
          this.setCaptchaToken(data.captcha_token);
          this.setChannelId(data.id);
          this.mallLoaded.next(this.mall);
          resolve(this.mall);
          this.addScript(this.mall.website_scripts);
        },
        error: (err) => {
          this.loadingMall = false;
          reject(err);
        },
      });
    });
  }

  /**
   * Returns the mall object from the server
   * @returns Observable<Mall>
   */
  getMall(): Observable<Mall> {
    // let url = `${environment.serverUrl}/mall/`;
    let url = `${environment.serverUrl}/osiris/customer/channel/`;
    return this.httpClient.get<Mall>(url, {}).pipe(retry(2));
  }

  setCaptchaToken(token: any) {
    environment.recaptcha.siteKey = token;
  }

  getAuthHeaders() {
    let sessionToken = window.sessionStorage.getItem('lite_token');
    console.debug('sessionToken', sessionToken);
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + sessionToken,
      }),
    };

    return httpOptions;
  }

  /**
   * Sets the page title
   * @param title
   */
  setPageTitle(title: string) {
    this.titleService.setTitle(title);
  }

  // getAds(contestId: number): Observable<Array<Ad>> {
  //   let params: any = {
  //     contest: contestId
  //   };
  //   let url = `${environment.serverUrl}/configuration/ads/`;
  //   return this.httpClient.get<Array<Ad>>(url, { params }).pipe(retry(2));
  // }

  // getFlowById(id: number): Observable<Flow> {
  // getFlowById(id: number){
  //     let url = `${environment.serverUrl}/configuration/flow/${id}`;
  //     return this.httpClient.get<Flow>(url).pipe(retry(2));
  // }

  // getFlowById(id: number): Observable<{ name: string; type: string; required: boolean; label: string; }[]> {
  getFlowById(): Observable<any> {
    let headers = this.getAuthHeaders();
    let url = `${environment.serverUrl}/horus/crm/profile/fields`;
    return this.httpClient.get<Flow>(url, headers).pipe(retry(2));
  }

  getProfileFieldsDefinition(): Observable<any> {
    let headers = this.getAuthHeaders();
    let url = `${environment.serverUrl}/horus/crm/profile/fields`;
    return this.httpClient.get<any>(url, headers).pipe(retry(2));
  }

  getUpdateDataFields(): Observable<any> {
    let headers = this.getAuthHeaders();
    let url = `${environment.serverUrl}/thoth/campaigns/update-data-fields`;
    return this.httpClient.get<any>(url, headers).pipe(retry(2));
  }

  getMaskedProfileFields(payload: any): Observable<any> {
    let headers = this.getAuthHeaders();
    let url = `${environment.serverUrl}/horus/crm/profile/masked-fields`;
    return this.httpClient.post<any>(url, payload, headers).pipe(retry(2));
  }

  updateProfileData(payload: any): Observable<any> {
    let headers = this.getAuthHeaders();
    let url = `${environment.serverUrl}/horus/crm/profile`;
    return this.httpClient.post<any>(url, payload, headers).pipe(retry(2));
  }

  updateIncrementalProfile(payload: any): Observable<any> {
    let headers = this.getAuthHeaders();
    let url = `${environment.serverUrl}/horus/crm/answers/update`;
    return this.httpClient.put<any>(url, payload, headers).pipe(retry(2));
  }

  getResponse(responses: any[], question: string): any {
    let found = responses.find((r) => r.question === question);
    return found ? found.response : null;
  }

  convertToKey(question: any): string {
    let str = String(question).toLowerCase().replace(/ /g, '_');

    switch (str) {
      case 'correo_electrónico':
        return 'email';
      case 'nombre':
        return 'first_name';
      case 'apellidos':
        return 'last_name';
      case 'celular':
        return 'phone';
      case 'fecha_de_nacimiento':
        return 'born_date';
      case 'genero':
        return 'gender';
      case 'acepto_recibir_notificaciones_por_correo_electrónico':
        return 'allow_promotional_emails';
      case 'acepto_recibir_notificaciones_por_sms':
        return 'allow_promotional_sms';
      default:
        return str;
    }
  }

  saveResponse(data: QuestionRequest[]): Observable<QuestionRequest[]> {
    let body: any = {};
    data.forEach((response) => {
      let key = this.convertToKey(response.question);
      let value = response.response;
      body[key] = value !== undefined && value !== '' ? value : null;
    });
    body['document_type'] = this.document_type;
    body['document'] = this.document_number;

    let headers = this.getAuthHeaders();
    let url = `${environment.serverUrl}/horus/crm/profile`;
    return this.httpClient
      .post<QuestionRequest[]>(url, body, headers)
      .pipe(retry(2));
    // return this.httpClient.post<QuestionRequest[]>(url, data).pipe(retry(2));
  }

  saveIncrementalResponse(
    data: QuestionIncrementalRequest[]
  ): Observable<QuestionRequest[]> {
    let body: any = {};
    // data.forEach((response) => {
    //   let key = this.convertToKey(response.question);
    //   let value = response.answer;
    //   if (value !== null && value !== undefined && value !== '') {
    //     body[key] = value;
    //   }
    // });
    let headers = this.getAuthHeaders();
    let url = `${environment.serverUrl}/horus/crm/answers/create`;
    return this.httpClient
      .post<QuestionRequest[]>(url, { answers: data }, headers)
      .pipe(retry(2));
    // return this.httpClient.post<QuestionRequest[]>(url, data).pipe(retry(2));
  }

  loadMallColors() {
    let colorBrands: any = {};
    if (this.mall && this.mall.brand_colors) {
      colorBrands = this.mall.brand_colors;
    } else if (this.mall && this.mall.customer?.brand_colors) {
      colorBrands = this.mall.customer.brand_colors;
    }
    console.debug('Loading mall colors', this.mall, colorBrands);
    for (let colorName in colorBrands) {
      let colorValue = colorBrands[colorName];
      try {
        this.loadWebsiteColor(colorName, colorValue);
      } catch (error) {
        console.warn('Error setting color', colorName, colorValue);
      }
    }
  }

  /**
   * This method sets the color variables in the website
   * @param colorName Name of the color (primary, secondary, tertiary, success, warning, danger, light, medium, dark)
   * @param colorValue Value of the color (hexadecimal)
   */
  loadWebsiteColor(colorName: string, colorValue: string) {
    let colorData = this.generateColor(colorValue);
    this.document.documentElement.style.setProperty(
      '--ion-color-' + colorName,
      colorData.value ?? null
    );
    this.document.documentElement.style.setProperty(
      '--ion-color-' + colorName + '-rgb',
      colorData.valueRgb ?? null
    );
    this.document.documentElement.style.setProperty(
      '--ion-color-' + colorName + '-contrast',
      colorData.contrast ?? null
    );
    this.document.documentElement.style.setProperty(
      '--ion-color-' + colorName + '-contrast-rgb',
      colorData.contrastRgb ?? null
    );
    this.document.documentElement.style.setProperty(
      '--ion-color-' + colorName + '-shade',
      colorData.shade ?? null
    );
    this.document.documentElement.style.setProperty(
      '--ion-color-' + colorName + '-tint',
      colorData.tint ?? null
    );
  }

  /**
   * This method generates the color variables by calculating the contrast, tint and shade of the color
   * @param value Value of the color (hexadecimal)
   * @returns
   */
  private generateColor(value: string): ColorVariable {
    const color = new Color(value);
    const contrast = color.contrast();
    const tint = color.tint();
    const shade = color.shade();
    const formattedValue = value.startsWith('#') ? value : `#${value}`;

    return {
      value: formattedValue,
      valueRgb: this.rgbToString(color.rgb),
      contrast: contrast.hex,
      contrastRgb: this.rgbToString(contrast.rgb),
      tint: tint.hex,
      shade: shade.hex,
    };
  }

  private rgbToString(c: RGB): string {
    return `${c.r},${c.g},${c.b}`;
  }

  // Method to dynamically add and execute a script in the head
  addScript(htmlScript: string) {
    if (!htmlScript) return; // Exit if no script is provided

    console.log('Appending to HEAD:', htmlScript);

    // Append the script to the head of the document
    document.head.insertAdjacentHTML('beforeend', htmlScript);

    // Replace all script tags within .customScript elements
    this.replaceScriptsInNodes(document.querySelectorAll('.customScript'));
  }

  // Replace script tags within the provided nodes
  replaceScriptsInNodes(nodes: any) {
    nodes.forEach(
      (node: {
        tagName: string;
        replaceWith: (arg0: HTMLScriptElement) => void;
      }) => {
        console.log('Processing node:', node);

        // Check if the node is a script tag, then clone and replace it
        if (node.tagName === 'SCRIPT') {
          console.log('Replacing script tag');
          node.replaceWith(this.cloneScript(node));
        }
      }
    );
  }

  // Clone the script tag to ensure it executes again
  cloneScript(node: any) {
    const script = document.createElement('script'); // Create new script element
    script.textContent = node.textContent; // Copy the content of the original script

    // Copy all attributes from the original node to the new script tag
    [...node.attributes].forEach((attr) =>
      script.setAttribute(attr.name, attr.value)
    );

    return script; // Return the new script element
  }


  setSiteKey(key: any) {
    this.siteKey = key;
  }

  getSiteKey(): any {
    return this.siteKey;
  }

  getUserDocumentTypeOptions(): DocumentTypeOption[] {
    const configuredTypes = this.mall?.customer?.user_document_types || [];
    const documentTypes = configuredTypes.reduce(
      (options: DocumentTypeOption[], documentType: UserDocumentType) => [
        ...options,
        ...this.normalizeUserDocumentType(documentType),
      ],
      []
    );

    return documentTypes.length
      ? documentTypes
      : [...this.defaultUserDocumentTypes];
  }

  getDefaultUserDocumentType(): string {
    const documentTypes = this.getUserDocumentTypeOptions();
    const citizenshipCard = documentTypes.find(
      (documentType) => documentType.value === 'CC'
    );

    return citizenshipCard?.value || documentTypes[0]?.value || 'CC';
  }

  private normalizeUserDocumentType(
    documentType: UserDocumentType
  ): DocumentTypeOption[] {
    const attrs = documentType?.attrs || documentType;
    const values = Array.isArray(attrs) ? attrs : [attrs];

    return values
      .filter((value) => value?.name && value?.code)
      .map((value) => ({
        label: value.name as string,
        value: value.code as string,
      }));
  }

  saveUserLoginData(document_type: string, document_number: string): void {
    this.document_type = document_type;
    this.document_number = document_number;

    localStorage.setItem('document_type', document_type);
    localStorage.setItem('document_number', document_number);
  }

  loadUserLoginData(): { document_type: string | null; document_number: string | null } {
    const document_type = localStorage.getItem('document_type');
    const document_number = localStorage.getItem('document_number');

    return { document_type, document_number };
  }

  clearUserLoginData(): void {
    localStorage.removeItem('document_type');
    localStorage.removeItem('document_number');
  }

  setChannelId(id: any): void {
    localStorage.setItem('channel_id', id);
  }
}
